/**
 * Required property-detail rules for the online scheduler's property review step.
 *
 * Previously only Foundation blocked progression, so orders reached
 * scheduling with empty or zero Square Footage / Year Built. Those two fields feed
 * the `modifier-sqft` and `modifier-age` price modifiers, so a missing value is not
 * cosmetic — it silently misprices the job. New construction is the common case:
 * Zillow has no parcel record for a new build, so the extractor yields `sqft: 0`.
 *
 * Number of Units is deliberately absent from the required set: it stays
 * warning-only and must not block progression.
 *
 * Kept in its own module (rather than inline in PropertyReview) so the blocking
 * predicate and the "needs verification" predicate have exactly one definition.
 * Divergent copies of this logic are what produced the bug in the first place:
 * the field-level warning icon and the summary chip disagreed about `sqft: 0`.
 */

import { AddressForScheduler } from '@/util/types/globalTypes';
import { toPropertyNumber } from '@/util/functions/property/toPropertyNumber';

/**
 * Smallest accepted square footage.
 *
 * Locked decision: `0` is invalid, so the floor is 1. A higher floor is still an
 * open product question — 62 historical records hold a literal `sqft = 1`, which
 * this rule admits. Raising this constant to 100 rejects those plus every other
 * junk value (2.3, 4.827, …) at a cost of 0.10% of historical records; nothing
 * else needs to change.
 */
export const MIN_SQFT = 100;

/**
 * Earliest accepted year built.
 *
 * Chosen from the real distribution rather than a round number: genuine colonial
 * properties in the data go back to 1635, so a stricter bound (e.g. 1800) would
 * reject ~96 legitimate historic homes. 1600 admits every real value on record
 * while still rejecting the junk entries (1, 1092, 1385).
 */
export const MIN_YEAR_BUILT = 1500;

/**
 * Latest accepted year built: next calendar year.
 *
 * New construction is routinely dated in the current or coming year, so the
 * ceiling must float rather than be hardcoded — a fixed year would start
 * rejecting legitimate new builds the moment the calendar passed it. Anything
 * beyond it is a typo (3650, 20000, 202622 all appear in historical data).
 */
export const getMaxYearBuilt = (): number => new Date().getFullYear() + 1;

/** Fields that block progression past the property review step. */
export type RequiredPropertyField = 'sqft' | 'yearBuilt' | 'foundation';

/**
 * Display order matches the on-screen form layout (SQFT, Year Built, Foundation)
 * so the error message reads in the same order the user scans the fields.
 */
const REQUIRED_FIELD_ORDER: RequiredPropertyField[] = [
  'sqft',
  'yearBuilt',
  'foundation',
];

export const REQUIRED_PROPERTY_FIELD_LABELS: Record<
  RequiredPropertyField,
  string
> = {
  sqft: 'Square Footage',
  yearBuilt: 'Year Built',
  foundation: 'Foundation',
};

/**
 * These read `unknown` rather than the declared field type on purpose. The
 * declared type is `number | undefined`, but a stringified value reaches this
 * rule often enough that trusting the type produced a bug of its own: a property
 * with a perfectly good Year Built kept reporting Year Built as missing, because
 * the form had stored `'1998'` instead of `1998`. `toPropertyNumber` is the one
 * place that decides what counts as a usable number — blank, `NaN` and junk text
 * all resolve to `undefined`, so a cleared field can never read as a zero.
 */
export const isSqftValid = (sqft: AddressForScheduler['sqft']): boolean => {
  const value = toPropertyNumber(sqft);
  return value !== undefined && value >= MIN_SQFT;
};

export const isYearBuiltValid = (
  yearBuilt: AddressForScheduler['yearBuilt']
): boolean => {
  const value = toPropertyNumber(yearBuilt);
  return (
    value !== undefined &&
    value >= MIN_YEAR_BUILT &&
    value <= getMaxYearBuilt()
  );
};

export const isFoundationValid = (
  foundation: AddressForScheduler['foundation']
): boolean => typeof foundation === 'string' && foundation.trim() !== '';

/**
 * Why a required field is blocking.
 *
 * `empty` and `out-of-range` are worth telling apart because they ask the
 * customer for different things. Treating both as "required" produced a bug of
 * its own: a property carrying `SQFT = 59` was told Square Footage was required
 * while the number sat in the field in front of them, with nothing on screen to
 * say that 59 is below the floor this rule enforces.
 */
export type RequiredPropertyFieldReason = 'empty' | 'out-of-range';

export type RequiredPropertyFieldIssue = {
  field: RequiredPropertyField;
  label: string;
  reason: RequiredPropertyFieldReason;
  /** A sentence for the on-page alert: "Square Footage must be at least 100 sq ft…" */
  message: string;
  /** A bullet for the tooltips, which have no room for prose: "Square Footage (at least 100 sq ft)" */
  summary: string;
};

/**
 * Every required field that is blocking, in form order, each with the reason and
 * the wording to show for it.
 *
 * This is the source the UI should read. The bounds live next to the constants
 * that define them, so changing `MIN_SQFT` or the year range rewrites what the
 * customer is told without anyone having to remember to update a string.
 */
export function getRequiredPropertyFieldIssues(
  address: AddressForScheduler | null | undefined
): RequiredPropertyFieldIssue[] {
  const empty = (field: RequiredPropertyField): RequiredPropertyFieldIssue => ({
    field,
    label: REQUIRED_PROPERTY_FIELD_LABELS[field],
    reason: 'empty',
    message: `${REQUIRED_PROPERTY_FIELD_LABELS[field]} is required.`,
    summary: REQUIRED_PROPERTY_FIELD_LABELS[field],
  });

  const outOfRange = (
    field: RequiredPropertyField,
    detail: string,
    bounds: string
  ): RequiredPropertyFieldIssue => ({
    field,
    label: REQUIRED_PROPERTY_FIELD_LABELS[field],
    reason: 'out-of-range',
    message: `${REQUIRED_PROPERTY_FIELD_LABELS[field]} ${detail}`,
    summary: `${REQUIRED_PROPERTY_FIELD_LABELS[field]} (${bounds})`,
  });

  if (!address) return REQUIRED_FIELD_ORDER.map(empty);

  const issues: RequiredPropertyFieldIssue[] = [];

  const sqft = toPropertyNumber(address.sqft);
  if (sqft === undefined || sqft <= 0) {
    // Zillow reports 0 for a property it holds no parcel record for, so a zero is
    // an absent value wearing a number's clothes — ask for the value rather than
    // telling the customer to raise 0 to the floor.
    issues.push(empty('sqft'));
  } else if (!isSqftValid(address.sqft)) {
    issues.push(
      outOfRange(
        'sqft',
        `must be at least ${MIN_SQFT} sq ft.`,
        `at least ${MIN_SQFT} sq ft`
      )
    );
  }

  const yearBuilt = toPropertyNumber(address.yearBuilt);
  if (yearBuilt === undefined || yearBuilt <= 0) {
    issues.push(empty('yearBuilt'));
  } else if (!isYearBuiltValid(address.yearBuilt)) {
    issues.push(
      outOfRange(
        'yearBuilt',
        `must be a year between ${MIN_YEAR_BUILT} and ${getMaxYearBuilt()}.`,
        `${MIN_YEAR_BUILT}–${getMaxYearBuilt()}`
      )
    );
  }

  if (!isFoundationValid(address.foundation)) {
    issues.push(empty('foundation'));
  }

  return issues;
}

/** Which required fields are empty or hold a value that cannot be priced. */
export function getMissingRequiredPropertyFields(
  address: AddressForScheduler | null | undefined
): RequiredPropertyField[] {
  return getRequiredPropertyFieldIssues(address).map((issue) => issue.field);
}

/** Human-readable labels for the required fields still needing attention. */
export function getMissingRequiredPropertyFieldLabels(
  address: AddressForScheduler | null | undefined
): string[] {
  return getRequiredPropertyFieldIssues(address).map((issue) => issue.label);
}

/**
 * One sentence per blocking field, each saying what that field needs.
 */
export function getRequiredPropertyFieldMessages(
  address: AddressForScheduler | null | undefined
): string[] {
  return getRequiredPropertyFieldIssues(address).map((issue) => issue.message);
}

/**
 * One short bullet per blocking field — for the Next / Schedule Now / stepper
 * tooltips, which list what is outstanding rather than explaining it.
 */
export function getRequiredPropertyFieldSummaries(
  address: AddressForScheduler | null | undefined
): string[] {
  return getRequiredPropertyFieldIssues(address).map((issue) => issue.summary);
}

/** True when the property review step must not advance. */
export function hasMissingRequiredPropertyData(
  address: AddressForScheduler | null | undefined
): boolean {
  return getMissingRequiredPropertyFields(address).length > 0;
}

/** Joins field labels into readable prose: "A", "A and B", "A, B, and C". */
export function formatFieldList(labels: string[]): string {
  if (labels.length <= 1) return labels[0] ?? '';
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}

export type RequiredPropertyFieldPrompt = {
  /** "Square Footage, Year Built, and Foundation are required." Empty when every issue is a bound. */
  requiredSentence: string;
  /** One per field holding a value we cannot price: "Square Footage must be at least 100 sq ft." */
  rangeSentences: string[];
  /** "Please complete these fields to continue." */
  actionSentence: string;
  /** The three above as the single paragraph the alerts render. */
  text: string;
};

/**
 * The customer-facing paragraph for the blocking fields.
 *
 * Empty fields keep the original wording — one prose list, one instruction —
 * because that is the overwhelmingly common case and it reads well. A field that
 * holds a value we cannot price earns its own sentence naming the bound: telling
 * someone a Square Footage of 59 is "required" while it sits in the field in
 * front of them explains nothing. The verb follows suit — nothing is being
 * completed when the field is already filled in.
 */
export function getRequiredPropertyFieldPrompt(
  address: AddressForScheduler | null | undefined
): RequiredPropertyFieldPrompt {
  const issues = getRequiredPropertyFieldIssues(address);

  const emptyLabels = issues
    .filter((issue) => issue.reason === 'empty')
    .map((issue) => issue.label);
  const rangeSentences = issues
    .filter((issue) => issue.reason === 'out-of-range')
    .map((issue) => issue.message);

  const requiredSentence = emptyLabels.length
    ? `${formatFieldList(emptyLabels)} ${
        emptyLabels.length === 1 ? 'is' : 'are'
      } required.`
    : '';

  const actionSentence = issues.length
    ? `Please ${rangeSentences.length ? 'update' : 'complete'} ${
        issues.length === 1 ? 'this field' : 'these fields'
      } to continue.`
    : '';

  return {
    requiredSentence,
    rangeSentences,
    actionSentence,
    text: [requiredSentence, ...rangeSentences, actionSentence]
      .filter(Boolean)
      .join(' '),
  };
}

/**
 * Fields that hold a usable value which was auto-populated from Zillow and has not
 * been confirmed by the user. These are advisory only — they never block.
 *
 * A required field that is missing or invalid is deliberately excluded here: it is
 * already reported as a blocking error, and listing it twice (once red, once
 * yellow) reads as two separate problems.
 *
 * Unit Count is included regardless of its value. It is warning-only, but the old
 * `address.unitCount && !verified` guard hid the chip whenever the count was `0`
 * or absent — precisely the case worth surfacing.
 */
export function getUnverifiedAddressFieldLabels(
  address: AddressForScheduler | null | undefined
): string[] {
  if (!address) return [];

  const labels: string[] = [];

  if (isSqftValid(address.sqft) && !address.sqftVerified) {
    labels.push(REQUIRED_PROPERTY_FIELD_LABELS.sqft);
  }
  if (isYearBuiltValid(address.yearBuilt) && !address.yearBuiltVerified) {
    labels.push(REQUIRED_PROPERTY_FIELD_LABELS.yearBuilt);
  }
  if (!address.unitCountVerified) {
    labels.push('Unit Count');
  }
  if (isFoundationValid(address.foundation) && !address.foundationVerified) {
    labels.push(REQUIRED_PROPERTY_FIELD_LABELS.foundation);
  }

  return labels;
}
